import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import {
  Check,
  Copy,
  Loader2,
  Search,
  X,
  ArrowRight,
  Globe,
  Star,
  ChevronDown,
} from "lucide-react";
import { useDeferredValue, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ApiError, api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCurrency } from "@/lib/currency";
import { useI18n } from "@/lib/i18n";
import { getServiceIconUrl, getServiceIcon } from "@/lib/hero_services";
import { getCountryFlag, getCountryName, matchesCountry } from "@/lib/countries";
import { CountryOption } from "@/components/country-option";
import { sortCountries, type CountrySortKey } from "@/lib/country-sort";
import { SITE_URL } from "@/lib/site-url";
import {
  useActivations,
  useCancelActivation,
  useRequestActivation,
  useServices,
} from "@/lib/queries";
import { copyToClipboard } from "@/lib/utils";
import { AVAILABILITY_ERROR_CODES, unavailableLabel } from "@/lib/availability";

export const Route = createFileRoute("/_app/dashboard")({
  component: SmsPage,
  head: () => ({
    meta: [
      { title: "SMS Activations — SmsEmpire" },
      {
        name: "description",
        content:
          "Browse 200+ services and rent a virtual phone number to receive SMS verification codes instantly. Telegram, WhatsApp, Discord and more.",
      },
      { property: "og:title", content: "SMS Activations — SmsEmpire" },
      {
        property: "og:description",
        content: "Rent virtual numbers for 200+ services. SMS codes in under 30 seconds.",
      },
      { property: "og:url", content: `${SITE_URL}/dashboard` },
      { property: "og:image", content: `${SITE_URL}/og/default.png` },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/dashboard` }],
  }),
});

// ─── Country Picker Modal ──────────────────────────────────────────────────────
type CountryEntry = {
  rank: number | null;
  country: number;
  country_name: string;
  price: number;
  count: number;
  display_price?: number;
  purchasable?: boolean;
  unavailable_reason?: string | null;
  // Admin-only: the backend omits these entirely for everyone else.
  provider_cost?: number;
  multiplier_used?: number;
  margin_abs?: number;
  margin_pct?: number;
  rule_applied?: string;
};

type CountriesResponse = {
  countries?: CountryEntry[];
  total?: number;
  purchasable_total?: number;
  admin?: {
    upstream_balance: number | null;
    affordability_cutoff: number | null;
    global_multiplier: number;
    blocked_by_balance: number;
  };
};

const SORT_OPTIONS: { key: CountrySortKey; labelKey: string }[] = [
  { key: "quality", labelKey: "dashboard.sort_quality" },
  { key: "price_asc", labelKey: "dashboard.sort_price_asc" },
  { key: "price_desc", labelKey: "dashboard.sort_price_desc" },
  { key: "alpha", labelKey: "dashboard.sort_alpha" },
];

function CountryPickerModal({
  service,
  onSelect,
  onClose,
}: {
  service: any;
  onSelect: (service: any, country: string) => void;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const { format } = useCurrency();
  const [countries, setCountries] = useState<CountryEntry[]>([]);
  const [adminInfo, setAdminInfo] = useState<CountriesResponse["admin"]>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<CountrySortKey>("quality");
  const [total, setTotal] = useState<number>(0);
  const [purchasableTotal, setPurchasableTotal] = useState<number>(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<CountriesResponse>(`/api/activations/countries/${service.code}`)
      .then((data) => {
        if (cancelled) return;
        if (data.countries?.length) {
          setCountries(data.countries);
          setTotal(data.total ?? data.countries.length);
          setPurchasableTotal(
            data.purchasable_total ?? data.countries.filter((c) => c.purchasable !== false).length,
          );
          setAdminInfo(data.admin);
        } else {
          setError(t("dashboard.no_numbers_service"));
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : t("common.error"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [service.code, t]);

  const filtered = useMemo(() => {
    const matching = !search.trim()
      ? countries
      : countries.filter((c) => matchesCountry(search, c.country, c.country_name));
    return sortCountries(matching, sortKey, (c) => getCountryName(c.country, c.country_name));
  }, [countries, search, sortKey]);

  return (
    // Radix Dialog rather than a hand-rolled overlay: it traps focus, closes on
    // Escape, restores focus to the trigger and applies the right ARIA roles. The
    // previous div was unusable with a keyboard or a screen reader.
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-w-lg gap-0 p-0 sm:max-w-lg">
        <div className="flex max-h-[80vh] flex-col">
          {/* Header */}
          <DialogHeader className="space-y-0 border-b border-border p-5 text-left">
            <div className="flex items-center gap-3">
              <div className="relative overflow-hidden flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <img
                  src={getServiceIconUrl(service.code)}
                  alt=""
                  aria-hidden
                  className="h-6 w-6 object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              </div>
              <div>
                <DialogTitle className="text-base font-bold">{service.name}</DialogTitle>
                <DialogDescription className="flex items-center gap-1 text-xs">
                  <Globe className="h-3 w-3" aria-hidden /> {t("dashboard.pick_country")}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {/* Info banner */}
          <div className="mx-5 mt-4 rounded-xl bg-primary/5 border border-primary/20 px-4 py-2.5 text-xs text-muted-foreground">
            <Star className="inline h-3 w-3 text-primary mr-1" />
            {total > 0 ? (
              <strong className="text-foreground">
                {t("dashboard.countries_available", { total: purchasableTotal || total })}
              </strong>
            ) : (
              t("common.loading")
            )}
            {" — "}
            {t("dashboard.quality_banner_desc")}
          </div>

          {/* Admin-only: the provider's own numbers, so a failure can be explained
              rather than guessed at. Never rendered for a normal user — the backend
              does not even send these fields. */}
          {adminInfo && (
            <div className="mx-5 mt-2 rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-2 text-[11px] text-amber-600 dark:text-amber-400">
              <strong>{t("admin.provider_view")}</strong> {t("admin.upstream_balance")}: $
              {adminInfo.upstream_balance?.toFixed(3) ?? "—"} · {t("admin.max_affordable")}: $
              {adminInfo.affordability_cutoff?.toFixed(3) ?? "—"} ·{" "}
              {t("admin.blocked_by_balance", { count: adminInfo.blocked_by_balance })}
            </div>
          )}

          {/* Search + sort */}
          <div className="px-5 mt-3 flex gap-2">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("dashboard.search_country")}
                aria-label={t("dashboard.search_country")}
                autoFocus
                className="w-full rounded-lg border border-border bg-muted/30 py-2 pl-8 pr-8 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  aria-label={t("common.cancel")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <select
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as CountrySortKey)}
              aria-label={t("dashboard.sort_label")}
              className="rounded-lg border border-border bg-muted/30 px-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/40"
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.key} value={opt.key}>
                  {t(opt.labelKey)}
                </option>
              ))}
            </select>
          </div>

          {/* List */}
          <div className="overflow-y-auto flex-1 p-3 space-y-1.5 mt-2">
            {loading && (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin mr-2" /> {t("dashboard.loading_countries")}
              </div>
            )}
            {error && <p className="text-center text-sm text-red-400 py-8">{error}</p>}
            {!loading && filtered.length === 0 && countries.length > 0 && (
              <p className="text-center text-sm text-muted-foreground py-8">
                {t("dashboard.no_countries_found")}
              </p>
            )}
            {!loading && !error && countries.length === 0 && (
              <p className="text-center text-sm text-muted-foreground py-8">
                {t("dashboard.no_numbers_service")}
              </p>
            )}
            {!loading &&
              filtered.map((c) => (
                <CountryOption
                  key={c.country}
                  country={c}
                  t={t}
                  format={format}
                  onSelect={() => onSelect(service, String(c.country))}
                />
              ))}
          </div>

          <div className="border-t border-border p-4">
            <button
              onClick={onClose}
              className="w-full rounded-xl border border-border py-2.5 text-sm text-muted-foreground transition-all hover:bg-muted hover:text-foreground"
            >
              {t("common.cancel")}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Service Card ──────────────────────────────────────────────────────────────
function ServiceCard({
  s,
  isPopular,
  pending,
  onActivate,
  onPickCountry,
}: {
  s: any;
  isPopular: boolean;
  /** True while this exact service is mid-purchase — blocks a double click from
   *  firing a second request before the first one lands. */
  pending: boolean;
  onActivate: (service: any) => void;
  onPickCountry: (service: any) => void;
}) {
  const { t } = useI18n();
  const { format } = useCurrency();

  return (
    <Card
      className={`group relative overflow-hidden p-5 transition-all ${
        isPopular
          ? "border-primary/20 bg-primary/5 hover:border-primary/50 hover:shadow-glow"
          : "border-border bg-card hover:border-primary/40 hover:shadow-glow"
      }`}
    >
      <div className="flex items-start justify-between">
        <div
          className={`relative overflow-hidden flex h-11 w-11 items-center justify-center rounded-xl text-xl ${
            isPopular
              ? "bg-gradient-primary text-primary-foreground shadow-glow"
              : "bg-primary/10 text-primary"
          }`}
        >
          <img
            src={getServiceIconUrl(s.code)}
            alt={s.name}
            className="h-6 w-6 object-contain z-10 relative"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
          <span className="absolute inset-0 flex items-center justify-center opacity-50 z-0">
            {getServiceIcon(s.code, "sms")}
          </span>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground">{t("sms.available")}</p>
          <p className="text-sm font-semibold">{s.count}</p>
        </div>
      </div>
      <h3 className={`mt-4 text-lg ${isPopular ? "font-bold" : "font-semibold"}`}>{s.name}</h3>
      <div className="mt-3">
        <p className="text-xs text-muted-foreground">{t("sms.price_from")}</p>
        <p className={`text-xl font-bold ${isPopular ? "text-primary" : ""}`}>
          {format(s.price_available)}
        </p>
        <p className="text-[11px] text-muted-foreground/60 mt-0.5">
          {t("dashboard.cheapest_country_price")}
        </p>
      </div>
      <div className="mt-4 flex gap-2">
        <Button
          size="sm"
          className={`flex-1 ${isPopular ? "bg-gradient-primary shadow-glow" : "bg-gradient-primary"}`}
          disabled={pending}
          onClick={() => onActivate(s)}
        >
          {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : t("sms.buy")}
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="flex items-center gap-1 px-2.5 border-primary/30 hover:border-primary/60 hover:bg-primary/5"
          title={t("dashboard.pick_by_country")}
          disabled={pending}
          onClick={() => onPickCountry(s)}
        >
          <Globe className="h-3.5 w-3.5" />
        </Button>
      </div>
    </Card>
  );
}

/** Service cards rendered per page in the catalogue. */
const PAGE_SIZE = 36;

// ─── Main Page ─────────────────────────────────────────────────────────────────
function SmsPage() {
  const { t } = useI18n();
  const { format } = useCurrency();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [countryPickerService, setCountryPickerService] = useState<any | null>(null);
  // Tracks which service is mid-purchase, so a double click cannot fire two purchases
  // for the same (or a different) service while the first is still in flight.
  const [activatingCode, setActivatingCode] = useState<string | null>(null);
  // Debounced so typing does not re-filter 200+ services on every keystroke.
  const deferredQuery = useDeferredValue(query);

  const { data: services = [] } = useServices();
  // Same cache the activations page uses — the dashboard no longer polls separately.
  const { data: allActivations = [] } = useActivations();
  const activations = useMemo(() => allActivations.slice(0, 10), [allActivations]);

  const requestActivation = useRequestActivation();
  const cancelActivation = useCancelActivation();

  const filtered = useMemo(() => {
    const needle = deferredQuery.trim().toLowerCase();
    if (!needle) return services;
    return services.filter((s) => s.name.toLowerCase().includes(needle));
  }, [deferredQuery, services]);

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  // A new search starts from the top; otherwise a narrow result set would inherit a
  // large page size and the "show more" button would look broken.
  useEffect(() => setVisibleCount(PAGE_SIZE), [deferredQuery]);

  async function activate(service: any, country?: string) {
    if (!user || activatingCode) return;
    setActivatingCode(service.code);
    try {
      // No confirmation step: picking a country (or hitting "Buy" for the best one by
      // quality) purchases immediately. The backend guarantees the price charged is
      // exactly what was quoted and never below its own cost, so there is nothing left
      // for a confirmation dialog to protect against.
      await requestActivation.mutateAsync({ serviceCode: service.code, country });
      toast.success(t("sms.number_requested", { service: service.name }));
      navigate({ to: "/activations" });
    } catch (error) {
      // Every provider failure used to arrive as one generic sentence. The backend now
      // sends a reason code, so the user is told what actually happened.
      if (error instanceof ApiError && error.code) {
        if (error.code === "INSUFFICIENT_BALANCE") {
          toast.error(t("sms.insufficient"));
        } else if (AVAILABILITY_ERROR_CODES.includes(error.code)) {
          toast.error(unavailableLabel(t, error.code));
        } else {
          toast.error(error.message || t("sms.request_failed"));
        }
      } else {
        toast.error(t("sms.request_failed"));
      }
    } finally {
      setActivatingCode(null);
    }
  }

  function handlePickCountry(service: any, country: string) {
    setCountryPickerService(null);
    void activate(service, country);
  }

  async function cancel(id: number) {
    try {
      const data = await cancelActivation.mutateAsync(id);
      toast.success(t("activations.cancelled_refund", { amount: format(data?.refunded ?? 0) }));
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t("activations.cancel_failed"));
    }
  }

  async function copy(text: string) {
    if (!text) return;
    const ok = await copyToClipboard(text);
    if (ok) {
      toast.success(t("sms.copied"));
    }
  }

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      {countryPickerService && (
        <CountryPickerModal
          service={countryPickerService}
          onSelect={handlePickCountry}
          onClose={() => setCountryPickerService(null)}
        />
      )}

      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t("sms.title")}</h1>
        <p className="mt-1 text-muted-foreground">{t("sms.subtitle")}</p>
      </div>

      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("sms.search_ph")}
          className="pl-9"
          aria-label={t("sms.search_ph")}
        />
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          {t("sms.empty_search")}
        </div>
      ) : (
        <div className="space-y-8">
          {!query && filtered.some((s) => s.is_popular) && (
            <section>
              <h2 className="mb-4 text-xl font-bold tracking-tight">{t("sms.popular")}</h2>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {filtered
                  .filter((s) => s.is_popular)
                  .map((s) => (
                    <ServiceCard
                      key={s.id}
                      s={s}
                      isPopular={true}
                      pending={activatingCode === s.code}
                      onActivate={(svc) => void activate(svc)}
                      onPickCountry={(svc) => setCountryPickerService(svc)}
                    />
                  ))}
              </div>
            </section>
          )}

          <section>
            <h2 className="mb-4 text-xl font-bold tracking-tight">
              {query ? t("dashboard.search_results") : t("dashboard.all_services")}
            </h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {/* Rendering all 200+ cards at once made the first paint and every
                  keystroke noticeably slow on mid-range phones. */}
              {filtered.slice(0, visibleCount).map((s) => (
                <ServiceCard
                  key={s.id}
                  s={s}
                  isPopular={false}
                  pending={activatingCode === s.code}
                  onActivate={(svc) => void activate(svc)}
                  onPickCountry={(svc) => setCountryPickerService(svc)}
                />
              ))}
            </div>
            {filtered.length > visibleCount && (
              <div className="mt-6 flex flex-col items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
                  className="min-w-48"
                >
                  {t("dashboard.show_more")}
                </Button>
                <p className="text-xs text-muted-foreground" aria-live="polite">
                  {t("dashboard.showing_count", {
                    shown: Math.min(visibleCount, filtered.length),
                    total: filtered.length,
                  })}
                </p>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

// ─── Cancel timer hook for dashboard ─────────────────────────────────────────
function useCancelTimer(createdAt: string, status: string) {
  const [secondsLeft, setSecondsLeft] = useState<number>(0);

  useEffect(() => {
    if (status !== "pending" || !createdAt) {
      setSecondsLeft(0);
      return;
    }
    const isoStr = createdAt.endsWith("Z") ? createdAt : createdAt + "Z";
    const created = new Date(isoStr).getTime();

    const updateTimer = () => {
      const elapsed = Math.floor((Date.now() - created) / 1000);
      const remaining = Math.max(0, 120 - elapsed);
      setSecondsLeft(remaining);
    };
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [createdAt, status]);

  return secondsLeft;
}

function DashboardActiveCard({
  activation: a,
  onCopy,
  onCancel,
  t,
}: {
  activation: any;
  onCopy: (text: string) => void;
  onCancel: (id: number) => void;
  t: (k: string) => string;
}) {
  const cancelSecondsLeft = useCancelTimer(a.created_at, a.status);
  const canCancel = cancelSecondsLeft === 0;
  const flag = getCountryFlag(a.country);
  const countryName = getCountryName(a.country);

  return (
    <div className="flex flex-wrap items-center gap-4 rounded-xl border border-border bg-card p-4">
      <div className="relative overflow-hidden flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary text-lg">
        <img
          src={getServiceIconUrl(a.service_code)}
          alt={a.service_code}
          className="h-6 w-6 object-contain z-10 relative"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
        <span className="absolute inset-0 flex items-center justify-center opacity-50 z-0">
          {getServiceIcon(a.service_code, "sms")}
        </span>
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold">{a.service_code.toUpperCase()}</p>
          {a.country && (
            <span className="inline-flex items-center gap-1 rounded bg-muted/40 px-1.5 py-0.5 text-xs text-muted-foreground font-medium">
              <span>{flag}</span>
              <span>{countryName}</span>
            </span>
          )}
        </div>
        <button
          onClick={() => onCopy(a.number || "")}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mt-0.5 font-mono text-xs"
        >
          {a.number || t("dashboard.waiting_number")} <Copy className="h-3 w-3" />
        </button>
      </div>

      {a.status === "pending" ? (
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 text-xs text-amber-400 font-medium">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {t("sms.pending")}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onCancel(a.id)}
            disabled={!canCancel}
            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 disabled:opacity-40 disabled:cursor-not-allowed text-xs px-2.5 py-1 font-medium transition-all"
            title={
              !canCancel
                ? `Disponible para cancelar en ${cancelSecondsLeft}s`
                : t("activations.cancel")
            }
          >
            <X className="h-3.5 w-3.5 mr-1" />
            {canCancel ? t("activations.cancel") : `${cancelSecondsLeft}s`}
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <button
            onClick={() => onCopy(a.code!)}
            className="flex items-center gap-2 rounded-lg bg-success/15 px-3 py-1.5 text-sm font-semibold text-success"
          >
            <Check className="h-4 w-4" /> {a.code}
          </button>
          <Button variant="ghost" size="icon" onClick={() => onCancel(a.id)}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
